<template>
    <vx-modal :visible="props.visible" @update:visible="emit('update:visible', $event)" size="lg" :padding="false">
        <template v-slot:header>
            <div class="pt-6 px-6">Add Resources</div>
        </template>
        <template v-slot:subheader>
            <div class="px-6">Choose the resources you want to add to your flow.</div>
        </template>

        <div v-if="!confirming" class="p-6 flex flex-col gap-4 lg:px-12 lg:py-10">
            <resource-add-section resourceType="segment">
                <resource-selectable resourceType="segment" v-model="resources.segment"> </resource-selectable>
            </resource-add-section>

            <resource-add-section resourceType="campaign">
                <resource-selectable resourceType="campaign" v-model="resources.campaign"> </resource-selectable>
            </resource-add-section>

            <resource-add-section resourceType="automation" data-test="resource-add-automation-section">
                <resource-selectable resourceType="automation" v-model="resources.automation"> </resource-selectable>
            </resource-add-section>
        </div>

        <div v-else-if="confirming" class="p-6 lg:px-12 lg:py-10">
            <!-- v-if visible triggers the mounted hook -->
            <flow-resource-dependencies
                v-if="props.visible"
                ref="dependenciesRef"
                action="add"
                :resources="selectedResources"
            ></flow-resource-dependencies>
            <vx-alert v-if="tooManyResourcesError || error" color="danger" class="mt-6" :closeable="false">{{
                tooManyResourcesError || error
            }}</vx-alert>
        </div>

        <template v-if="!confirming" v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button @click="clearAndClose" type="button" class="grow lg:w-20 lg:grow-0" size="lg" color="muted">
                    Cancel
                </vx-button>
                <vx-button
                    @click="checkDependencies()"
                    data-test="add-resources"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Add to Flow
                    <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
        <template v-else v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="
                        confirming = false;
                        error = '';
                    "
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Back
                </vx-button>
                <vx-button
                    @click="confirm()"
                    :disabled="confirmDisabled"
                    :loading="saving"
                    data-test="confirm-resource-add"
                    type="button"
                    class="grow"
                    size="lg"
                    color="primary"
                >
                    Confirm
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script setup>
import { faCirclePlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxModal, VxAlert } from '@voxie/frontend-components';
import { computed, reactive, ref } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { useFlow } from '../useFlow';
import ResourceAddSection from './ResourceAddSection.vue';
import ResourceSelectable from './ResourceSelectable.vue';
import FlowResourceDependencies from '../FlowResourceDependencies.vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import { firstToUpperCase } from '~/utils/string';

const toasted = useToasted();

const flow = useFlow();

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
    flowSection: {
        type: Number,
        required: true,
    },
});

const confirming = ref(false);
const saving = ref(false);

const error = ref('');

const emit = defineEmits(['update:visible']);

const dependenciesRef = ref();

const resources = reactive({
    automation: [],
    campaign: [],
    segment: [],
});

const clearAndClose = () => {
    confirming.value = false;
    saving.value = false;
    resources.automation = [];
    resources.campaign = [];
    resources.segment = [];
    error.value = '';

    emit('update:visible', false);
};

const selectedResources = computed(() => {
    const selectedResources = [];

    resources.automation.forEach((automation) => {
        selectedResources.push({
            id: automation.id,
            type: 'automation',
        });
    });

    resources.campaign.forEach((campaign) => {
        selectedResources.push({
            id: campaign.id,
            type: 'campaign',
        });
    });

    resources.segment.forEach((segment) => {
        selectedResources.push({
            id: segment.id,
            type: 'segment',
        });
    });

    return selectedResources;
});

const confirmDisabled = computed(() => {
    return Boolean(
        saving.value ||
            !dependenciesRef.value ||
            dependenciesRef.value.loading ||
            dependenciesRef.value.truncatedResource ||
            tooManyResourcesError.value
    );
});

const tooManyResourcesError = computed(() => {
    const truncatedResource = dependenciesRef.value?.truncatedResource;
    if (truncatedResource) {
        return `${firstToUpperCase(truncatedResource.type)} ${truncatedResource.id} (${
            truncatedResource.name
        }) contains too many resources`;
    } else if (totalResourcesCount.value > 100) {
        return `At most 100 resources can be placed in a flow. ${totalResourcesCount.value} resources present.`;
    }
    return '';
});

const totalResourcesCount = computed(() => {
    const existingCount = flow.sections.value.reduce((count, section) => count + section.resources.length, 0);
    return existingCount + selectedResources.value.length + (dependenciesRef.value?.list?.length || 0);
});

const confirm = async () => {
    if (confirmDisabled.value) {
        return;
    }

    try {
        saving.value = true;
        await flow.addResources(props.flowSection, [...selectedResources.value, ...dependenciesRef.value.list]);
        clearAndClose();
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        error.value = e.response?.data?.message;
    } finally {
        saving.value = false;
    }
};

const checkDependencies = async () => {
    if (!selectedResources.value.length) {
        toasted.error('You must select a resource to add to the Flow');
        return;
    }
    confirming.value = true;
};
</script>
